import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import './CheckListWidget.css';

/**
 * A reusable checkbox list component with scrollable content
 * @param {Object} props - Component props
 * @param {Array<{id: string, display: string}>} props.items - Array of items to display
 * @param {Set<string>} props.selectedItems - Set of selected item IDs
 * @param {function} props.onItemChange - Callback function when an item is selected/deselected
 * @param {string} props.emptyMessage - Message to display when no items are available
 * @returns {JSX.Element} Scrollable checkbox list
 */
function CheckBoxList({ items, selectedItems, onItemChange, emptyMessage }) {
  return (
    <div className="space-y-2 mt-3 pr-2 max-h-72 overflow-y-auto slim-scrollbar">
      {items.length > 0 ? (
        items.map((item) => (
          <div key={item.id} className="form-control">
            <label className="py-1 cursor-pointer label">
              <span
                className={`label-text text-sm
                ${
                  selectedItems.has(item.id)
                    ? 'font-bold text-black'
                    : 'text-slate-950'
                }
                `}
              >
                {item.display}
              </span>
              <input
                type="checkbox"
                className="custom-checkbox"
                checked={selectedItems.has(item.id)}
                onChange={(e) => onItemChange(item.id, e.target.checked)}
              />
            </label>
          </div>
        ))
      ) : (
        <div className="py-2 text-gray-500 text-sm text-center">
          {emptyMessage}
        </div>
      )}
    </div>
  );
}

/**
 * A search input component with clear functionality
 * @param {Object} props - Component props
 * @param {string} props.value - Current search input value
 * @param {function} props.onChange - Function to handle input value changes
 * @param {string} props.placeholder - Placeholder text for the input
 * @param {function} props.onClear - Function to clear the input value
 * @returns {JSX.Element} Search input with clear button
 */
function SearchInput({ value, onChange, placeholder, onClear }) {
  return (
    <div className="relative form-control">
      <div className="relative">
        <input
          type="text"
          placeholder={placeholder}
          className="bg-gray-100 pr-8 pl-2 border border-gray-400 focus:border-2 focus:border-gray-400 rounded-sm focus:outline-none w-full placeholder:text-gray-400 placeholder:text-xs placeholder:uppercase input-sm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <MagnifyingGlassIcon className="top-1/2 right-3 bottom-1/2 absolute w-4 h-4 text-gray-500 -translate-y-1/2" />
        {value && (
          <button
            className="top-1/2 right-8 absolute hover:bg-gray-300 p-0.5 rounded-full text-gray-400 hover:text-gray-700 -translate-y-1/2"
            onClick={onClear}
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * A reusable checklist widget component that combines search and checkbox list functionality
 * @param {Object} props - Component props
 * @param {string} props.title - Title of the checklist widget
 * @param {string} props.searchPlaceholder - Placeholder text for search input
 * @param {Array<{id: string, display: string}>} props.items - Array of items to display
 * @param {Set<string>} props.selectedItems - Set of selected item IDs
 * @param {function} props.onItemChange - Callback function when an item is selected/deselected
 * @param {function} props.onClearAll - Function to clear all selected items
 * @param {function} props.onSelectAll - Function to select all items
 * @returns {JSX.Element} Checklist widget with search and checkbox list
 */
export default function CheckListWidget(props) {
  const {
    title,
    searchPlaceholder,
    items,
    selectedItems,
    onItemChange,
    onClearAll,
    onSelectAll
  } = props;

  const [searchValue, setSearchValue] = useState('');

  // Filter items based on search value
  const filteredItems = items.filter((item) =>
    item.display.toLowerCase().includes(searchValue.toLowerCase())
  );

  return (
    <div className="collapse collapse-arrow shadow-sm border-none rounded-none">
      <input type="checkbox" defaultChecked />
      <div className="collapse-title font-medium">
        <h4 className="font-[550] text-[#1688BE] uppercase">{title}</h4>
      </div>
      <div className="collapse-content border-gray-300 border-b-2 !rounded-none">
        <div className="space-y-2">
          <SearchInput
            value={searchValue}
            onChange={setSearchValue}
            onClear={() => setSearchValue('')}
            placeholder={searchPlaceholder}
          />
        </div>
        <CheckBoxList
          items={filteredItems}
          selectedItems={selectedItems}
          onItemChange={onItemChange}
          emptyMessage={`No ${title.toLowerCase()} found`}
        />

        <div className="space-x-2 space-y-2 mt-2.5 text-center">
          {selectedItems.size > 0 && (
            <button
              onClick={onClearAll}
              className="gap-1 bg-transparent hover:bg-[#F1FFC2] px-2 py-3.5 border border-[#92A846] font-[650] text-[#92A846] transition-all duration-400 ease-in-out cursor-pointer badge badge-ghost"
            >
              Clear {title} <XMarkIcon className="my-0.5 w-3 h-3" />
            </button>
          )}
          {selectedItems.size < items.length && (
            <button
              onClick={onSelectAll}
              className="gap-1 bg-transparent hover:bg-[#F1FFC2] px-3 py-3.5 border border-[#92A846] font-[650] text-[#92A846] transition-all duration-400 ease-in-out cursor-pointer badge badge-ghost"
            >
              Select All
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
